import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { AIExtractedIndicator } from '@/components/postgrid/common/AIExtractedIndicator';
import { WizardStep } from '@/components/postgrid/wizard';
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';

const identitySchema = z
  .object({
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    company_name: z.string().optional(),
  })
  .refine((data) => {
    return Boolean(data.first_name || data.last_name || data.company_name);
  }, 'Please provide a name or company');

export type IdentityStepData = z.infer<typeof identitySchema>;

interface ContactIdentityStepProps {
  wizardData?: Partial<IdentityStepData>;
  updateStepData?: (data: Record<string, any>) => void;
  markStepComplete?: (isValid: boolean) => void;
  /** Set of field names that were AI-extracted */
  aiExtractedFields?: Set<string>;
}

export const ContactIdentityStep: React.FC<ContactIdentityStepProps> = ({
  wizardData = {},
  updateStepData,
  markStepComplete,
  aiExtractedFields = new Set(),
}) => {
  const {
    register,
    watch,
    formState: { errors, isValid },
  } = useForm<IdentityStepData>({
    resolver: zodResolver(identitySchema),
    mode: 'onChange',
    defaultValues: {
      first_name: wizardData.first_name ?? '',
      last_name: wizardData.last_name ?? '',
      company_name: wizardData.company_name ?? '',
    },
  });

  const watched = watch();

  useEffect(() => {
    updateStepData?.(watched);
  }, [watched, updateStepData]);

  useEffect(() => {
    markStepComplete?.(isValid);
  }, [isValid, markStepComplete]);

  return (
    <WizardStep
      title="Who is this?"
      description="Tell us the person's name and/or the company they represent."
    >
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <Fieldset>
          <FieldGroup className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field>
              <div className="flex items-center gap-2">
                <Label>First Name</Label>
                <AIExtractedIndicator
                  show={aiExtractedFields.has('first_name')}
                />
              </div>
              <Input {...register('first_name')} placeholder="John" />
              {errors.first_name && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.first_name.message}
                </p>
              )}
            </Field>

            <Field>
              <div className="flex items-center gap-2">
                <Label>Last Name</Label>
                <AIExtractedIndicator
                  show={aiExtractedFields.has('last_name')}
                />
              </div>
              <Input {...register('last_name')} placeholder="Smith" />
              {errors.last_name && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.last_name.message}
                </p>
              )}
            </Field>
          </FieldGroup>

          <div className="mt-6">
            <Field>
              <div className="flex items-center gap-2">
                <Label>Company / Organization</Label>
                <AIExtractedIndicator
                  show={aiExtractedFields.has('company_name')}
                />
              </div>
              <Input {...register('company_name')} placeholder="Acme Corp" />
              {errors.company_name && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.company_name.message}
                </p>
              )}
            </Field>
          </div>
        </Fieldset>
      </div>
    </WizardStep>
  );
};
